// §73 GPS trip recorder – the imperative browser-API leaf, called BY Datastar.
//
// Datastar owns everything it can: the drive-view shell holds the signals, the
// periodic tick + flush (data-on-interval → declarative @post of the points signal),
// the connectivity beacon (navigator.onLine + a last-successful-fetch timer), and
// all rendering. This module does ONLY what Datastar can't – the geolocation watch
// and the Screen Wake Lock – and reports each fix back through `report` (a Datastar
// arrow) into the signals. Every network POST is a declarative @post in an arrow
// Datastar hands in; there is no fetch here.
//
// Window-helper + DriveReport types live in globals.d.ts.

interface Fix {
  lat: number;
  lng: number;
  acc: number;
}

const GEO_OPTS: PositionOptions = { enableHighAccuracy: true, maximumAge: 1000, timeout: 15000 };
// Mirror the server-side live-mode noise gate (trips.go) for the derived speed.
const GATE_ACC_M = 30;
const GATE_MIN_MOVE_M = 8;

function currentFix(): Promise<Fix | null> {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy }),
      () => resolve(null),
      GEO_OPTS,
    );
  });
}

function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371;
  const rad = Math.PI / 180;
  const dLat = (bLat - aLat) * rad;
  const dLng = (bLng - aLng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(aLat * rad) * Math.cos(bLat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

// bearing is the initial great-circle heading a→b, degrees clockwise from north.
// Derived from the moved leg (device compass heading is unreliable at rest); feeds
// the drive-map heading cone via the report.
function bearing(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const rad = Math.PI / 180;
  const y = Math.sin((bLng - aLng) * rad) * Math.cos(bLat * rad);
  const x = Math.cos(aLat * rad) * Math.sin(bLat * rad) - Math.sin(aLat * rad) * Math.cos(bLat * rad) * Math.cos((bLng - aLng) * rad);
  return (Math.atan2(y, x) / rad + 360) % 360;
}

interface DriveSession {
  tripId: string;
  report: (v: DriveReport) => void;
  watchId: number;
  wakeLock: any; // WakeLockSentinel – typed loosely for lib.dom portability
  curKmh: number;
  maxKmh: number;
  accM: number;
  wake: boolean;
  heading: number; // degrees from north, -1 until the first moved leg
  lastLat: number | null; // anchor for derived speed + heading
  lastLng: number | null;
  lastFixMs: number | null;
  lastSeenMs: number;
}
let live: DriveSession | null = null;

async function acquireWakeLock(): Promise<any> {
  try {
    const wl = (navigator as any).wakeLock;
    if (wl) return await wl.request('screen');
  } catch {
    // denied or unsupported – live tracking still works, the screen may just dim
  }
  return null;
}

// push reports the current session state into the signals (p = the raw point to
// buffer, or null for a non-fix update such as a wake-lock change).
function push(p: { lat: number; lng: number; acc: number } | null): void {
  if (!live) return;
  live.report({ s: live.curKmh, m: live.maxKmh, a: live.accM, w: live.wake, t: live.lastSeenMs, p, h: live.heading });
}

function onFix(pos: GeolocationPosition): void {
  if (!live) return;
  const c = pos.coords;
  const now = Date.now();
  live.accM = c.accuracy;
  live.lastSeenMs = now;
  const canGate = c.accuracy <= GATE_ACC_M;
  const moved = canGate && live.lastLat != null && live.lastLng != null
    ? haversineKm(live.lastLat, live.lastLng, c.latitude, c.longitude)
    : 0;
  const isLeg = canGate && live.lastLat != null && moved * 1000 >= GATE_MIN_MOVE_M;
  let kmh = live.curKmh;
  if (c.speed != null && c.speed >= 0) {
    kmh = c.speed * 3.6; // device speed when available
  } else if (isLeg && live.lastFixMs != null) {
    const dtH = (now - live.lastFixMs) / 3600000;
    if (dtH > 0) kmh = moved / dtH; // derive from the leg
  }
  if (!isFinite(kmh) || kmh < 0 || kmh > 400) kmh = live.curKmh; // reject glitches, keep last
  live.curKmh = kmh;
  if (kmh > live.maxKmh) live.maxKmh = kmh;
  // Heading: prefer the moved-leg bearing when a gated leg fires – it's the most
  // reliable, derived from real displacement. Otherwise fall back to the device's
  // own heading whenever it reports one and we're moving (the `c.speed` guard keeps
  // a resting compass's noise out), so the cone keeps turning even when the accuracy
  // gate stops legs from firing; `heading < 0` still lets it seed the first fix.
  if (isLeg) {
    live.heading = bearing(live.lastLat as number, live.lastLng as number, c.latitude, c.longitude);
  } else if (c.heading != null && !isNaN(c.heading) && ((c.speed ?? 0) > 0.5 || live.heading < 0)) {
    live.heading = c.heading;
  }
  if (canGate) {
    live.lastLat = c.latitude;
    live.lastLng = c.longitude;
    live.lastFixMs = now;
  }
  // The raw fix is buffered (into the $_drvPoints signal) for the declarative /points
  // flush; the server noise-gates it. Metrics are the client's smooth readout, and the
  // durable `_drvLast`/heading drive the <drive-map> trail.
  push({ lat: c.latitude, lng: c.longitude, acc: c.accuracy });
}

// data-init: start the imperative watch/wake loop and bind the signal setter.
window.__driveStart = (tripId, report) => {
  if (live && live.tripId === tripId) {
    // Re-rendered shell (resume/minimise): re-bind the setter to the running watch.
    live.report = report;
    push(null);
    return;
  }
  if (live) __driveStopInternal();
  live = {
    tripId, report, watchId: 0, wakeLock: null, curKmh: 0, maxKmh: 0, accM: -1, wake: false,
    heading: -1, lastLat: null, lastLng: null, lastFixMs: null, lastSeenMs: Date.now(),
  };
  live.watchId = navigator.geolocation.watchPosition(onFix, () => {}, GEO_OPTS);
  void acquireWakeLock().then((wl) => {
    if (live) {
      live.wakeLock = wl;
      live.wake = !!wl;
      push(null);
    }
  });
  push(null);
};

// data-on:visibilitychange__document: a wake lock auto-releases when hidden.
window.__driveVisible = () => {
  if (document.visibilityState !== 'visible' || !live || live.wakeLock) return;
  void acquireWakeLock().then((wl) => {
    if (live) {
      live.wakeLock = wl;
      live.wake = !!wl;
      push(null);
    }
  });
};

function __driveStopInternal(): void {
  if (!live) return;
  navigator.geolocation.clearWatch(live.watchId);
  if (live.wakeLock) void live.wakeLock.release?.().catch(() => {});
  live = null;
}

// Called from the Stopp button's done arrow, after the stop @post fires: stop the
// watch + release the wake lock (the trip is now closed server-side). The shell is
// kept so the summary flourish (§73 Inc C, shown via `$_drvDone`) can play over the
// frozen trail; `__driveClose` removes it when the driver taps Fertig.
window.__driveStop = () => {
  __driveStopInternal();
};

// Called from the summary's Fertig button: remove the full-screen shell.
window.__driveClose = () => {
  document.getElementById('drive-view')?.remove();
};

// __getFix captures one fix and hands it to `arrow` (which @posts); `done` always
// runs (clears the loading signal) – even if the fix is denied.
window.__getFix = (arrow, done) => {
  void currentFix()
    .then((f) => {
      if (f) arrow(f.lat, f.lng, f.acc);
    })
    .finally(() => done?.());
};

window.__getFixCb = (cb) => {
  void currentFix().then((f) => {
    if (f) cb(f.lat, f.lng);
  });
};

// __tripRecord opens the record-mode chooser, captures a fix for the GPS modes, and
// hands (mode, lat, lng, acc) to `dispatch` (which @posts the start). `done` always
// clears the loading signal.
window.__tripRecord = async (btn, dispatch, done) => {
  try {
    let cfg: {
      title?: string;
      options?: { id: string; label: string; desc?: string }[];
      clientStep?: { title?: string; options?: { id: string; label: string }[] };
    };
    try {
      cfg = JSON.parse(btn.dataset.recordConfig ?? '{}');
    } catch {
      return;
    }
    const mode = await window.__choose(cfg.title ?? '', cfg.options ?? []);
    if (!mode) return;
    if (mode === 'gps' || mode === 'live') {
      // §73 Inc 7: pick who the route is for BEFORE opening the live screen – a second
      // chooser the server emits only when billing is on and clients exist. __choose
      // returns '' for the leading "Privatfahrt" option (start unattributed) and null
      // only on dismiss, so a dismiss cancels the whole start while '' proceeds. This
      // MUST test `=== null`, not `!picked` ('' is falsy but a valid pick).
      let clientId = '';
      if (cfg.clientStep) {
        const picked = await window.__choose(cfg.clientStep.title ?? '', cfg.clientStep.options ?? []);
        if (picked === null) return; // dialog dismissed → cancel the start
        clientId = picked;
      }
      const f = await currentFix();
      dispatch(mode, f ? f.lat : null, f ? f.lng : null, f ? f.acc : null, clientId);
    } else {
      dispatch('odo', null, null, null, '');
    }
  } finally {
    done?.();
  }
};

window.__fmtDur = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${h}:${pad(m)}:${pad(sec)}`;
};

export {};
